import React, { useState, useCallback, useRef } from 'react';
import { 
  IoCloseOutline, 
  IoCloudUploadOutline,
  IoLogoGoogle
} from 'react-icons/io5';
import { useDarkMode } from '../context/DarkModeContext';

interface WorkExperienceDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, displayLabel: string) => Promise<void>;
  onGoogleDriveSelect?: () => Promise<void>;
}

const COMMON_LABELS = [
  'Offer Letter',
  'Performance Review',
  'Certificate',
  'Project Document',
  'Reference Letter',
  'Award',
];

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const WorkExperienceDocumentUploadModal: React.FC<WorkExperienceDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onGoogleDriveSelect,
}) => {
  const { darkMode } = useDarkMode();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [displayLabel, setDisplayLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setSelectedFile(null);
    setDisplayLabel('');
    setError(null);
    setIsUploading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload a PDF, Word document, Excel spreadsheet, or image.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 10MB limit.';
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError(null);
    // Use filename without extension as initial display label
    const labelSuggestion = file.name.replace(/\.[^/.]+$/, '');
    setDisplayLabel(labelSuggestion);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !displayLabel.trim()) return;

    try {
      setIsUploading(true);
      setError(null);
      await onUpload(selectedFile, displayLabel.trim());
      handleClose();
    } catch (err) {
      setError('Failed to upload file. Please try again.');
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div 
        className={`
          w-full max-w-lg rounded-lg shadow-xl p-6
          ${darkMode ? 'bg-gray-800' : 'bg-white'}
          transform transition-all
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Add Document
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Close modal"
          >
            <IoCloseOutline className="w-6 h-6" />
          </button>
        </div>

        {/* Upload Methods */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => fileInputRef.current?.click()}
            className={`
              flex-1 py-2 px-4 rounded-lg font-medium transition-colors
              ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-700/80' : 'bg-gray-100 text-gray-900 hover:bg-gray-100/80'}
            `}
          >
            Upload File
          </button>
          {onGoogleDriveSelect && (
            <button
              onClick={onGoogleDriveSelect}
              className={`
                flex-1 py-2 px-4 rounded-lg font-medium
                bg-blue-500 text-white
                hover:bg-blue-600 transition-colors
                flex items-center justify-center gap-2
              `}
              disabled={isUploading}
            >
              <IoLogoGoogle className="w-5 h-5" />
              Link from Drive
            </button>
          )}
        </div>

        {/* Drag & Drop Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-8 mb-6 text-center cursor-pointer
            transition-colors duration-200
            ${isDragging 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20' 
              : darkMode 
                ? 'border-gray-600 hover:border-gray-500' 
                : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileInput}
            accept={ACCEPTED_FILE_TYPES.join(',')}
            aria-label="Choose a file to upload"
          />
          <IoCloudUploadOutline className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          {selectedFile ? (
            <div>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedFile.name}
              </p>
              <p className="text-sm text-gray-500">
                Click or drag and drop to change file
              </p>
            </div>
          ) : (
            <div>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Drag & Drop Files or Click to Browse
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Supported formats: PDF, Word, Excel, JPEG, PNG (max 10MB)
              </p>
            </div>
          )}
        </div>

        {/* Display Label Input */}
        <div className="mb-6">
          <label 
            htmlFor="displayLabel" 
            className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}
          >
            Display Label
          </label>
          <input
            id="displayLabel"
            type="text"
            value={displayLabel}
            onChange={(e) => setDisplayLabel(e.target.value)}
            placeholder="Enter custom label..."
            className={`
              w-full px-3 py-2 rounded-lg
              border ${darkMode ? 'border-gray-600' : 'border-gray-300'}
              ${darkMode ? 'bg-gray-700' : 'bg-white'}
              ${darkMode ? 'text-white' : 'text-gray-900'}
              focus:outline-none focus:ring-2 focus:ring-blue-500
            `}
          />
        </div>

        {/* Common Labels */}
        <div className="mb-6">
          <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Common Labels:
          </p>
          <div className="flex flex-wrap gap-2">
            {COMMON_LABELS.map((label) => (
              <button
                key={label}
                onClick={() => setDisplayLabel(label)}
                className={`
                  px-3 py-1 rounded-full text-sm transition-colors
                  ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-700/80' : 'bg-gray-100 text-gray-700 hover:bg-gray-100/80'}
                `}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 text-red-500 text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleClose}
            className={`
              px-4 py-2 rounded-lg
              ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}
            `}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || !displayLabel.trim() || isUploading}
            className={`
              px-4 py-2 rounded-lg
              bg-blue-500 text-white
              hover:bg-blue-600 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center gap-2
            `}
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              'Add Document'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkExperienceDocumentUploadModal; 
